/* ARCELF — Hero
   - measure sticky header height → set --header-h on each hero (so hero block fits below header)
   - slideshow rotation (configurable interval, pauses when tab hidden or hero off-screen)
   - is-scrolled state on <html>: toggled when window has scrolled past the top
       drives the CSS transition that shrinks the fixed wordmark from 740 → 110 (and back)
   - smooth-scroll for in-page anchors (back to top)
*/
(function () {
  'use strict';

  const heroes = document.querySelectorAll('[data-arcelf-hero]');
  if (heroes.length) heroes.forEach(initHero);

  /* ============================================================
     is-scrolled state — single source of truth for the wordmark morph
     ============================================================ */
  const SCROLL_THRESHOLD = 1; // px — any scroll triggers the morph
  let lastScrolled = null;
  const updateScrollState = () => {
    const scrolled = window.scrollY > SCROLL_THRESHOLD;
    if (scrolled === lastScrolled) return;
    lastScrolled = scrolled;
    document.documentElement.classList.toggle('is-scrolled', scrolled);
  };
  window.addEventListener('scroll', updateScrollState, { passive: true });
  updateScrollState();

  /* ============================================================
     smooth-scroll for any in-page anchor (e.g. back to top)
     ============================================================ */
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || href === '#') return;
    const target = document.querySelector(href);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (history.pushState) history.pushState(null, '', href);
  });

  function initHero(hero) {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* --- measure sticky header → expose as --header-h on the hero --- */
    const header = document.querySelector('.arcelf-header--sticky') || document.querySelector('.arcelf-header');
    const updateHeaderVar = () => {
      const h = header ? header.getBoundingClientRect().height : 0;
      hero.style.setProperty('--header-h', `${Math.round(h)}px`);
    };
    updateHeaderVar();
    window.addEventListener('resize', updateHeaderVar);
    if ('ResizeObserver' in window && header) {
      new ResizeObserver(updateHeaderVar).observe(header);
    }

    /* --- slideshow --- */
    const slides = Array.from(hero.querySelectorAll('.arcelf-hero-slide'));
    if (slides.length > 1) {
      const styleInterval = parseInt(getCSSVar(hero, '--hero-slide-interval'), 10);
      const interval = Number.isFinite(styleInterval) && styleInterval > 1000 ? styleInterval : 6000;
      let current = 0;
      let timer = null;

      const advance = () => {
        slides[current].classList.remove('is-active');
        current = (current + 1) % slides.length;
        slides[current].classList.add('is-active');
      };

      const start = () => {
        if (timer || prefersReduced) return;
        timer = window.setInterval(advance, interval);
      };
      const stop = () => {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      };

      start();

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop(); else start();
      });

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
          entries.forEach(e => { e.isIntersecting ? start() : stop(); });
        }, { rootMargin: '0px 0px -20% 0px' });
        io.observe(hero);
      }
    }

    /* --- scroll-morph: drive --p (0 → 1) across the pinned stage ---
       giant wordmark shrinks/rises into the small top-page wordmark as the
       sticky hero is scrolled through (CSS reads --p). */
    if (!prefersReduced) {
      let morphTick = false;
      const setProgress = () => {
        morphTick = false;
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const travel = hero.offsetHeight - vh;
        if (travel <= 0) { hero.style.setProperty('--p', '0'); return; }
        let p = -hero.getBoundingClientRect().top / travel;
        if (p < 0) p = 0; else if (p > 1) p = 1;
        hero.style.setProperty('--p', p.toFixed(4));
      };
      const onMorph = () => {
        if (!morphTick) { morphTick = true; requestAnimationFrame(setProgress); }
      };
      window.addEventListener('scroll', onMorph, { passive: true });
      window.addEventListener('resize', onMorph, { passive: true });
      setProgress();
    }
  }

  function getCSSVar(el, name) {
    return getComputedStyle(el).getPropertyValue(name).trim();
  }
})();
